
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  shallowRef,
  watch,
} from "vue";
import ActivityModal from "@/views/user/modal/ActivityModal.vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import VZSideDrawer from "@/components/content/VZSideDrawer.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { usePagination } from "@/helpers/pagination";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { SiteApi } from "@/services/api/Site.service";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import { DrawerComponent } from "@/assets/ts/components";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import {
  siteDevicesTableBulkDelete,
  siteDevicesTableFilters,
  siteDevicesTableHeaders,
  SITE_DEVICES_TABS,
  SITE_LOOKUP,
} from "./siteDataTableOptions";
import { ISiteDevices } from "@/interfaces/iSite";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { getLookups } from "@/services/api/Lookup.service";
import * as Yup from "yup";
import { useToast } from "vue-toastification";
import { hasPermission } from "@/core/services/permission.service";
import { ITabConfig } from "@/interfaces/iTabConfig";
import { LocationAPI } from "@/services/api/Location.service";

export default defineComponent({
  components: { Datatable, VZSideDrawer, VZDeleteModal, ActivityModal },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const addMessage = t("pages.site.deviceAddedMessage");
    const editMessage = t("pages.site.deviceUpdatedMessage");
    const datatable = ref<null | HTMLFormElement>(null);
    const { page, total, rowsPerPage } = usePagination();
    const loading = ref(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showDeleteDialog = ref<any>(false);
    const showSearch = true;
    const tableData = ref<ISiteDevices[]>([]);
    const selectedForAction = ref<Partial<ISiteDevices>>({});
    const localPreference = ref({});
    const bulkDelete = ref<IBulkDelete>(siteDevicesTableBulkDelete());
    const tableHeader = ref(siteDevicesTableHeaders());
    const tableFilter = ref(siteDevicesTableFilters());
    const tabs = shallowRef<ITabConfig[]>(SITE_DEVICES_TABS());
    const sideDrawerTitle = ref<string>("");
    const sideDrawerIcon = ref<string>("");
    const openDrawer = ref<boolean>(false);
    const format = computed(() => {
      return FormatMethods;
    });
    let options: { label: any; value: number }[] = [];
    let siteOptions: { label: any; value: number }[] = [];
    let locationOptions = ref<{ label: string; value: number }[]>();
    onMounted(async () => {
      setCurrentPageBreadcrumbs(t("pages.site.device"), ["Home", "Devices"]);
      loading.value = true;
      // queryUpdate({
      //   ...defaultFilter.value,
      //   page: page.value - 1,
      //   size: rowsPerPage.value,
      // });
      await getLookups(
        "static-data/lookupsData?lookupName=LKDeviceType&status=ACTIVE"
      )
        .then((res: any) => {
          const data = res.content;
          data.map((obj) => {
            options.push({
              label: obj.text,
              value: +obj.code,
            });
          });
        })
        .catch((err) => {
          return [];
        });
      await getLookups(SITE_LOOKUP.url)
        .then((res) => {
          res.map((data) => {
            siteOptions.push({
              label: data.text,
              value: data.value,
            });
          });
        })
        .catch((err) => {
          console.error(err);
        });
      expandMenu();
    });
    const tableRowClick = (item) => {
      tabs.value = tabs.value.map((tab) => {
        return { ...tab, props: { ...tab.props, data: item } };
      });
      sideDrawerTitle.value = item.name;
      sideDrawerIcon.value = "bi bi-cpu";
      DrawerComponent?.getInstance("vz__drawer")?.toggle();
    };
    const editSite = (item: any) => {
      if (item.siteDevices.length > 0 && item.siteDevices[0].site_id !== null) {
        item.siteDeviceModelForDeviceCreation = `${item.siteDevices[0].site_id}`;
      } else {
        item.siteDeviceModelForDeviceCreation = "";
      }
      if (item.siteDeviceModelForDeviceCreation) {
        updatedLocationValue(item.siteDeviceModelForDeviceCreation);
      }
      formData.value.data = item;
      formData.value.edit = true;
      if (datatable.value) {
        datatable.value.openAddEditModal = true;
      }
    };
    const updatedLocationValue = async (value) => {
      // locationOptions
      try {
        // locationOptions
        formData.value.model[5].loading = true;
        const data = await LocationAPI.getInstance().siteLocations({
          siteId: value,
        });
        locationOptions.value = data.content.map((data) => {
          return {
            label: data.locationName,
            value: +data.webId,
          };
        });
      } catch (err) {
        console.log(err);
      } finally {
        formData.value.model[5].loading = false;
      }
    };
    let formData = ref<IAddEditFormModel>({
      page: "Site Devices",
      header: "Device",
      edit: false,
      data: null,
      defaultValues: {
        status: "Active",
      },
      label: [{ addHeader: t("pages.site.addDevice") }],
      validationSchema: Yup.object({
        name: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .required(t("common.name") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.name") + " " + t("common.isRequired")),
        macAddress: Yup.string()
          .max(20, getMaxLengthErrorMessage(t("pages.site.macAddress"), 20))
          .matches(
            /^([0-9A-Fa-f]{2}:){5}([0-9A-Fa-f]{2})|([0-9a-fA-F]{4}\\.[0-9a-fA-F]{4}\\.[0-9a-fA-F]{4})$/,
            t("pages.site.macAddress") + " " + t("validation.customValidation")
          )
          .required(t("pages.site.macAddress") + " " + t("common.isRequired")),

        startDate: Yup.date()
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .min(Yup.ref("startDate"), t("validation.greaterEndDate"))
          .nullable(),
        uuId: Yup.string()
          .required(t("pages.site.uuid") + " " + t("common.isRequired"))
          .matches(/\S/, t("pages.site.uuid") + " " + t("common.isRequired"))
          .nullable(),
        type: Yup.string().required(
          t("common.types") + " " + t("common.isRequired")
        ),
        siteDeviceModelForDeviceCreation: Yup.string().required(
          t("common.sites") + " " + t("common.isRequired")
        ),
        siteLocationId: Yup.string()
          .nullable()
          .required(t("pages.location.title") + " " + t("common.isRequired")),
        status: Yup.string().required(
          t("common.status") + " " + t("common.isRequired")
        ),
      }),
      model: [
        {
          name: "name",
          label: t("common.name"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "macAddress",
          label: t("pages.site.macAddress"),
          type: "text",
          placeholder: "00:00:00:00:00:00",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "uuId",
          label: t("pages.site.uuid"),
          type: "text",
          placeholder: "UUID",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "type",
          label: t("common.types"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select type",
          options,
        },
        {
          name: "startDate",
          label: t("common.startDate"),
          type: "date",
          required: true,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "endDate",
          label: t("common.endDate"),
          type: "date",
          required: false,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "siteDeviceModelForDeviceCreation",
          label: t("pages.form.sites"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select branch",
          options: siteOptions,
          multiSelect: false,
          dependentFields: ["siteLocationId"],
          onValueChange: updatedLocationValue,
        },
        {
          name: "siteLocationId",
          label: t("pages.location.title"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select location",
          options: locationOptions.value ?? [],
          multiSelect: false,
          loading: false,
        },
        {
          name: "status",
          label: t("common.status"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: true,
          placeholder: "Please select status",
          options: [
            {
              label: t("common.active"),
              value: "Active",
            },
            {
              label: t("common.inactive"),
              value: "Inactive",
            },
          ],
        },
      ],
    });
    const computedFormModel = computed(() => {
      return {
        ...formData.value,
        model: formData.value.model.map((model) => {
          if (model.name === "siteLocationId") {
            return { ...model, options: locationOptions.value ?? [] };
          }
          return model;
        }),
      };
    });
    let permissionModel = ref<any>({
      create: "VYZOR:INTEGRATIONS:DEVICES:CREATE",
      delete: "VYZOR:INTEGRATIONS:DEVICES:DELETE",
      update: "VYZOR:INTEGRATIONS:DEVICES:UPDATE",
    });
    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdate(params);
    };

    const queryUpdate = async (params?: Record<string, number | string>) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        const data = await SiteApi.getInstance().getSiteDevicesList({
          ...params,
        });
        localPreference.value = { ...params };
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } finally {
        loading.value = false;
      }
    };
    const submit = async (reqData) => {
      reqData.siteDeviceModelForDeviceCreation = [
        { site: reqData.siteDeviceModelForDeviceCreation },
      ];
      loading.value = true;
      reqData = formData.value.edit ? reqData : { ...reqData };
      let message = formData.value.edit ? editMessage : addMessage;
      const { siteDevices, locationId, siteName, ...result } = reqData;
      delete result.status;
      await SiteApi.getInstance()
        .addSiteDevicesList(result, formData.value.edit)
        .then(() => {
          toast.success(message);
          queryUpdate({
            ...localPreference.value,
            page: page.value - 1,
            size: rowsPerPage.value,
          });
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
          formData.value.edit = false;
        });
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate({ ...localPreference.value });
    };
    const formCancel = () => {
      formData.value.edit = false;
      locationOptions.value = [];
    };

    const expandMenu = () => {
      let menuItems = document.querySelectorAll(".side-menu-class");
      menuItems[6].className =
        "menu-link accordion-button border-0 bg-transparent py-4 shadow-none text-hover-white side-menu-class";
      let childItems = document.querySelectorAll(
        ".site-menu-item-class, .collapse"
      );
      childItems.forEach((element) => {
        if (element.id == "menu-section-6") {
          element.className =
            "accordion-collapse menu-item site-menu-item-class collapse show";
        }
      });
    };
    return {
      bulkDelete,
      format,
      tableData,
      tableHeader,
      sideDrawerIcon,
      tableFilter,
      showSearch,
      page,
      total,
      rowsPerPage,
      defaultFilter,
      loading,
      selectedForAction,
      formData,
      submit,
      syncQuery,
      refreshList,
      editSite,
      permissionModel,
      hasPermission,
      tabs,
      tableRowClick,
      sideDrawerTitle,
      t,
      expandMenu,
      openDrawer,
      datatable,
      computedFormModel,
      formCancel,
      showDeleteDialog,
    };
  },
});
